import React, { useState, useRef } from 'react';
import { API_ENDPOINTS } from '../config/apiRouting';
import { useAuth } from '../contexts/AuthContext';

interface ImageUploadProps {
  uploadType: 'employee' | 'menu' | 'supplier' | 'marketing' | 'general';
  entityId?: string;
  currentImageUrl?: string;
  onUploadSuccess?: (imageUrl: string) => void;
  onUploadError?: (error: Error) => void;
  maxSizeMB?: number;
  acceptedFormats?: string[];
}

const ImageUpload: React.FC<ImageUploadProps> = ({
  uploadType,
  entityId,
  currentImageUrl,
  onUploadSuccess,
  onUploadError,
  maxSizeMB = 10,
  acceptedFormats = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
}) => {
  const { getIdToken } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState<string | null>(currentImageUrl || null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateFile = (file: File): string | null => {
    // Check file size
    const maxSize = maxSizeMB * 1024 * 1024;
    if (file.size > maxSize) {
      return `File size must be less than ${maxSizeMB}MB`;
    }

    // Check file type
    if (!acceptedFormats.includes(file.type)) {
      return `File type must be one of: ${acceptedFormats.join(', ')}`;
    }

    return null;
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result as string);
    };
    reader.readAsDataURL(file);

    // Upload file
    await uploadFile(file);
  };

  const readFileAsBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(reader.error || new Error('File read failed'));
      reader.onload = () => {
        const result = reader.result as string;
        const comma = result.indexOf(',');
        resolve(comma >= 0 ? result.slice(comma + 1) : result);
      };
      reader.readAsDataURL(file);
    });

  const resolveEndpoint = (): string => {
    switch (uploadType) {
      case 'employee':
        return API_ENDPOINTS.uploadEmployee(entityId);
      case 'menu':
        if (!entityId) throw new Error('Menu item ID is required for menu image upload');
        return API_ENDPOINTS.uploadMenu(entityId);
      case 'supplier':
        if (!entityId) throw new Error('Supplier ID is required for supplier image upload');
        return API_ENDPOINTS.uploadSupplier(entityId);
      case 'marketing':
        return API_ENDPOINTS.uploadMarketing();
      case 'general':
      default:
        return API_ENDPOINTS.uploadGeneral(entityId);
    }
  };

  const getAuthToken = async (): Promise<string | null> => {
    try {
      const token = await getIdToken();
      if (token) return token;
    } catch {
      /* fall through to storage fallback for non-Cognito setups */
    }
    return (
      sessionStorage.getItem('idToken') ||
      localStorage.getItem('idToken') ||
      sessionStorage.getItem('authToken') ||
      localStorage.getItem('authToken')
    );
  };

  const uploadFile = async (file: File) => {
    setUploading(true);
    setError(null);
    setUploadProgress(0);

    try {
      const endpoint = resolveEndpoint();
      const base64Data = await readFileAsBase64(file);
      setUploadProgress(50);

      const token = await getAuthToken();
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          fileName: file.name,
          fileType: file.type,
          fileSize: file.size,
          base64Data
        })
      });

      const payload = await response.json().catch(() => null);
      if (!response.ok || !payload?.success) {
        throw new Error(payload?.message || payload?.error || `Upload failed with status ${response.status}`);
      }

      const url: string | undefined = payload.data?.url || payload.url;
      if (!url) throw new Error('Upload did not return a url');

      setUploadProgress(100);
      setPreview(url);
      onUploadSuccess?.(url);
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      setError(error.message);
      onUploadError?.(error);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleRemoveImage = () => {
    setPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="image-upload-container">
      <div className="image-upload-wrapper">
        {preview ? (
          <div className="image-preview">
            <img 
              src={preview} 
              alt="Preview" 
              style={{ 
                maxWidth: '100%', 
                maxHeight: '300px',
                borderRadius: '8px',
                objectFit: 'cover'
              }} 
            />
            {!uploading && (
              <div className="image-actions" style={{ marginTop: '12px' }}>
                <button 
                  className="btn btn-outline"
                  onClick={() => fileInputRef.current?.click()}
                  style={{ marginRight: '8px' }}
                >
                  Change Image
                </button>
                <button 
                  className="btn btn-outline"
                  onClick={handleRemoveImage}
                >
                  Remove
                </button>
              </div>
            )}
          </div>
        ) : (
          <div 
            className="upload-area"
            onClick={() => fileInputRef.current?.click()}
            style={{
              border: '2px dashed #ddd',
              borderRadius: '8px',
              padding: '40px',
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'all 0.3s',
              backgroundColor: '#f8f9fa'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#007bff';
              e.currentTarget.style.backgroundColor = '#e7f3ff';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = '#ddd';
              e.currentTarget.style.backgroundColor = '#f8f9fa';
            }}
          >
            <div style={{ marginBottom: '16px' }}>
              <div style={{ fontSize: '48px', color: '#6c757d' }}>↑</div>
            </div>
            <p style={{ margin: '0 0 8px 0', fontWeight: '600' }}>
              Click to upload image
            </p>
            <p style={{ margin: 0, fontSize: '14px', color: '#6c757d' }}>
              or drag and drop
            </p>
            <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#6c757d' }}>
              Max size: {maxSizeMB}MB
            </p>
          </div>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept={acceptedFormats.join(',')}
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />

        {uploading && (
          <div className="upload-progress" style={{ marginTop: '16px' }}>
            <div style={{ marginBottom: '8px', fontSize: '14px' }}>
              Uploading... {uploadProgress}%
            </div>
            <div 
              style={{ 
                width: '100%', 
                height: '8px', 
                backgroundColor: '#e9ecef',
                borderRadius: '4px',
                overflow: 'hidden'
              }}
            >
              <div 
                style={{ 
                  width: `${uploadProgress}%`,
                  height: '100%',
                  backgroundColor: '#007bff',
                  transition: 'width 0.3s ease'
                }}
              />
            </div>
          </div>
        )}

        {error && (
          <div 
            className="upload-error" 
            style={{ 
              marginTop: '16px',
              padding: '12px',
              backgroundColor: '#f8d7da',
              color: '#721c24',
              borderRadius: '4px',
              fontSize: '14px'
            }}
          >
            {error}
          </div>
        )}
      </div>
    </div>
  );
};

export default ImageUpload;